import { SHOW_SALON_GACHA_TICKETS } from '../../../../constants/featureFlags';
import { getGachaTickets } from 'api/gacha-tickets';
import SetTicketSelection, {
  validSetTickets,
  hasInvalidSetTickets,
} from './SetTicketSelection';
import { useCallback, useEffect, useState } from 'react';
import { queryKeys } from '../../../../constants';
import {
  TChannelProjectDetail,
  TProjectFemale,
  TReleaseFemaleProject,
} from 'types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getProjectPrice, projectRelease } from 'api';
import FreeGirl from './FreeGirl';
import Confirm from './Confirm';
import IconButton from 'components/IconButton';
import { useChatStore, useUserStore } from 'store';
import { toast } from 'react-toastify';

enum EReleaseStep {
  FREE_GIRL = 1,
  CONFIRM = 2,
  PAYMENT = 3,
}

type Props = {
  females?: TProjectFemale[];
  projectData: TChannelProjectDetail & { projectId: number };
  isFetching: boolean;
  refetchFemales: () => void;
  refetchData: () => void;
};

function ReleaseGirl({
  females = [],
  isFetching,
  refetchData,
  projectData,
}: Props) {
  // State
  const [step, setStep] = useState<EReleaseStep>(EReleaseStep.FREE_GIRL);
  const [releaseGirls, setReleaseGirls] = useState<TProjectFemale[]>([]);

  const [tickets, setTickets] = useState<
    NonNullable<TReleaseFemaleProject['setGachaTickets']>
  >([]);
  const [checking, setChecking] = useState(false);
  const queryClient = useQueryClient();
  const userId = useUserStore((state) => state.user?.id);
  useEffect(() => {
    setTickets([]);
  }, [userId, projectData.projectId]);
  // Hook
  const { setActionChatStep } = useChatStore();

  const {
    data: estimatePrice,
    isFetching: estimating,
    isError: estimateError,
  } = useQuery({
    queryKey: [
      queryKeys.PROJECT_PRICE,
      projectData.projectId,
      ...releaseGirls.map(({ user }) => user.id),
      ...(SHOW_SALON_GACHA_TICKETS ? [userId, JSON.stringify(tickets)] : []),
    ],
    queryFn: () =>
      getProjectPrice({
        projectId: projectData.projectId,
        femaleIds: releaseGirls.map(({ user }) => user.id),
        ...(SHOW_SALON_GACHA_TICKETS && tickets.length
          ? { setGachaTickets: tickets }
          : {}),
      }),
    enabled: !!releaseGirls?.length,
  });

  const walletKey = [queryKeys.SALON_GACHA_TICKETS, userId];
  const {
    data: wallet,
    isFetching: fetchingWallet,
    isPending: walletPending,
    isError: walletError,
    refetch: refetchWallet,
  } = useQuery({
    queryKey: walletKey,
    queryFn: ({ signal }) => getGachaTickets(signal),
    enabled:
      SHOW_SALON_GACHA_TICKETS &&
      step === EReleaseStep.CONFIRM &&
      !!estimatePrice?.setGachaTicketEligible &&
      !!userId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const selectionValid =
    !SHOW_SALON_GACHA_TICKETS ||
    !tickets.length ||
    (!walletError &&
      !hasInvalidSetTickets(tickets, wallet) &&
      JSON.stringify(
        validSetTickets(tickets, estimatePrice?.females || [], wallet, true),
      ) === JSON.stringify(tickets));
  const refreshTickets = async () => {
    setTickets([]);
    await queryClient.invalidateQueries({ queryKey: walletKey });
  };

  const { mutateAsync: release, isPending: isReleasing } = useMutation({
    mutationFn: (body: TReleaseFemaleProject) => {
      return projectRelease(body);
    },
  });

  const onSuccess = useCallback(() => {
    setActionChatStep();
    refetchData();
  }, [refetchData, setActionChatStep]);

  const handleRelease = async () => {
    if (
      estimating ||
      estimateError ||
      !estimatePrice ||
      checking ||
      isReleasing ||
      !selectionValid
    )
      return;
    setChecking(true);
    try {
      if (SHOW_SALON_GACHA_TICKETS && tickets.length) {
        const fresh = await queryClient.fetchQuery({
          queryKey: walletKey,
          queryFn: ({ signal }) => getGachaTickets(signal),
          staleTime: 0,
        });
        if (
          hasInvalidSetTickets(tickets, fresh) ||
          JSON.stringify(
            validSetTickets(tickets, estimatePrice.females, fresh, true),
          ) !== JSON.stringify(tickets)
        ) {
          await refreshTickets();
          toast.error(
            '選択した券が使えなくなりました。見積りを取り直して再確認してください。',
          );
          return;
        }
      }
      const result = await release({
        femaleIds: releaseGirls.map(({ user }) => user.id),
        projectId: projectData.projectId,
        ...(SHOW_SALON_GACHA_TICKETS && tickets.length
          ? {
              setGachaTickets: tickets,
              expectedTotalAfterDiscount:
                estimatePrice.payment.totalAfterDiscount,
            }
          : {}),
      });
      if (SHOW_SALON_GACHA_TICKETS) void refreshTickets();
      if (result.isFinished) toast.success('お会計が完了しました');
      onSuccess();
    } catch (error: any) {
      if (SHOW_SALON_GACHA_TICKETS && error?.response?.status === 409) {
        await refreshTickets();
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.PROJECT_PRICE, projectData.projectId],
        });
        toast.error('券と金額を取り直しました。もう一度ご確認ください。');
      } else toast.error(error.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div>
      {step !== EReleaseStep.FREE_GIRL && (
        <IconButton
          icon="chevronLeft"
          transparent
          disabled={checking || isReleasing}
          onClick={() => {
            setStep((prev) => prev - 1);
          }}
          className="absolute top-5"
        />
      )}
      {step === EReleaseStep.FREE_GIRL && (
        <FreeGirl
          loading={isFetching}
          defaultValues={releaseGirls}
          females={females?.filter((female: any) => female.isMeeting)}
          onSubmit={(girls) => {
            if (isFetching) return;
            setTickets([]);
            setReleaseGirls(girls);
            setStep(EReleaseStep.CONFIRM);
          }}
          isSingleMeeting={!!projectData.isSingleMeeting}
        />
      )}
      {step === EReleaseStep.CONFIRM && estimateError && (
        <p role="alert">
          見積りを取得できませんでした。
          <button
            type="button"
            onClick={() => {
              setTickets([]);
              void queryClient.invalidateQueries({
                queryKey: [queryKeys.PROJECT_PRICE, projectData.projectId],
              });
            }}
          >
            見積りを取り直す
          </button>
        </p>
      )}
      {step === EReleaseStep.CONFIRM && (
        <Confirm
          loading={estimating}
          loadingRelease={isReleasing || checking}
          disabled={
            estimateError ||
            !estimatePrice ||
            !selectionValid ||
            (SHOW_SALON_GACHA_TICKETS && !!tickets.length && fetchingWallet)
          }
          ticketSelection={
            SHOW_SALON_GACHA_TICKETS &&
            estimatePrice?.setGachaTicketEligible ? (
              <SetTicketSelection
                data={estimatePrice}
                wallet={wallet}
                value={tickets}
                onChange={setTickets}
                disabled={isReleasing || checking || estimating}
                pending={walletPending}
                error={walletError}
                retry={() => {
                  void refetchWallet();
                }}
              />
            ) : undefined
          }
          data={estimatePrice}
          onSubmit={handleRelease}
          projectData={projectData}
        />
      )}
    </div>
  );
}

export default ReleaseGirl;
